<!--
  Authorize.svelte — Application consent / authorization page.

  Shown after login when an external application redirected the user to
  AbstractID for authentication.  Displays the application's name, logo,
  and description, and asks the user to approve or deny access.

  Supports two flows:

  ### Legacy flow (non-OAuth)
    1. External app redirects user to `/authorize?app=UUID&returnURL=...`
    2. If not logged in → redirect to `/login?app=UUID&redirect=...`
    3. After login → redirect back here with the same query params
    4. This page fetches public app info and shows the consent card
    5. On approve → calls authorize API → redirects to returnURL with token
    6. On deny → redirects to returnURL without token (or shows message)

  ### OAuth 2.0 Authorization Code + PKCE flow
    1. Client app redirects user to `/oauth/authorize?response_type=code&client_id=...&...`
    2. Server validates params and redirects here with `?oauth=true&client_id=...&...`
    3. If not logged in → redirect to `/login?app=UUID&redirect=...`
    4. After login → redirect back with all OAuth params
    5. On approve → calls POST /oauth/authorize → redirects to redirect_uri with ?code=...&state=...
    6. On deny → redirects to redirect_uri with ?error=access_denied

  Also supports a `previewConsent=true` query param for admins to preview the
  consent page without actually authorizing.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import { Button, Spinner } from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import { push, querystring } from '../../abstract-spa-router';
	import AuthLayout from './AuthLayout.svelte';
	import { AlertMessage, LoadingOverlay } from '../../components';
	import {
		getApplicationPublicInfo,
		authorizeForApplication,
		createOAuthAuthorizationCode,
		checkApplicationAuthorization,
		serverLogout
	} from '../../AppNetwork';
	import { getToken, clearToken, refreshTokenNow, tokenData } from '../../Auth';
	import site from '../../site';
	import type { ApplicationPublicInfo } from '../../AppTypes';
	import { t, t2 } from '../../i18n';

	// ── State ─────────────────────────────────────────────────

	/** The API base URL for building image paths. */
	const apiBase = site.backend.baseURL;

	/** Application UUID to authorize for. */
	let appUUID = '';
	/** URL to redirect back to after authorization. */
	let returnURL = '';
	/** Whether this is a preview from the admin panel (no actual auth). */
	let isPreview = $state(false);

	// ── OAuth 2.0 params ──────────────────────────────────────
	/** Whether this is an OAuth 2.0 authorization code flow. */
	let isOAuth = $state(false);
	/** OAuth client_id (application UUID). */
	let oauthClientId = '';
	/** OAuth redirect_uri. */
	let oauthRedirectUri = '';
	/** OAuth scope (space-separated). */
	let oauthScope = $state('');
	/** OAuth code_challenge (PKCE). */
	let oauthCodeChallenge = '';
	/** OAuth code_challenge_method (S256). */
	let oauthCodeChallengeMethod = '';
	/** OAuth state (opaque, returned unchanged). */
	let oauthState = '';
	/** OIDC nonce (included in ID token). */
	let oauthNonce = '';

	/** Loaded public application info. */
	let appInfo: ApplicationPublicInfo | null = $state(null);
	/** Whether the initial load is in progress. */
	let loading = $state(true);
	/** Whether the authorize action is in progress. */
	let authorizing = $state(false);
	/** Error message to display. */
	let errorMessage = $state('');

	/** Result after authorization: 'approved', 'pending', or null. */
	let authResult: 'approved' | 'pending' | null = $state(null);

	/** Whether the user has previously authorized this app (for "continue" flow). */
	let alreadyAuthorized = $state(false);

	/** Whether an account switch (logout → re-login) is in progress. */
	let switchingAccount = $state(false);

	/** Token to display on the staging page when returnURL=token. */
	let tokenDisplay = $state('');
	/** Whether the token was just copied to clipboard (flash feedback). */
	let copied = $state(false);
	/**
	 * URL the page is currently navigating to. When set, the UI renders a
	 * "click here if you are not redirected" fallback so users can recover
	 * from popup blockers, slow networks, or other situations where
	 * `window.location.href = …` silently fails to navigate.
	 */
	let pendingRedirectUrl = $state('');
	/**
	 * Gates the "Redirecting…" UI behind a short timer so fast redirects
	 * don't flash the message before the browser navigates away.
	 */
	let showPendingRedirect = $state(false);
	/** Placeholder JWT shown in the preview token flow. */
	const previewToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IlByZXZpZXcgVG9rZW4ifQ.placeholder';

	// ── Initialization ────────────────────────────────────────

	onMount(async () => {
		/* All params come from the URL query string. No sessionStorage fallback. */
		const params = new URLSearchParams($querystring || '');
		appUUID = params.get('app') || '';
		returnURL = params.get('returnURL') || '';
		isPreview = params.get('previewConsent') === 'true';
		console.log('[Authorize] onMount entry', {
			querystring: $querystring,
			pathname: window.location.pathname,
			href: window.location.href,
			app: appUUID,
			returnURL,
			hasToken: !!getToken()
		});

		/*
		 * `logout=true` lets a consumer app force account-switching at the IdP:
		 * we revoke the current session (best-effort), clear the local token,
		 * strip the flag from the URL so refresh/back doesn't loop, and then
		 * fall through to the normal flow which redirects to /login.
		 */
		if (params.get('logout') === 'true') {
			params.delete('logout');
			if (getToken()) {
				try {
					await serverLogout();
				} catch {
					/* Token may already be invalid server-side; continue with local cleanup. */
				}
				clearToken();
			}
			const cleaned = params.toString();
			const newUrl = `${window.location.pathname}${cleaned ? `?${cleaned}` : ''}`;
			window.history.replaceState({}, '', newUrl);
		}

		/* Detect OAuth flow. */
		isOAuth = params.get('oauth') === 'true';
		/** Extract OAuth-specific parameters when running the OAuth flow. */
		if (isOAuth) {
			oauthClientId = params.get('client_id') || '';
			oauthRedirectUri = params.get('redirect_uri') || '';
			oauthScope = params.get('scope') || 'openid';
			oauthCodeChallenge = params.get('code_challenge') || '';
			oauthCodeChallengeMethod = params.get('code_challenge_method') || 'S256';
			oauthState = params.get('state') || '';
			oauthNonce = params.get('nonce') || '';
			/* In OAuth flow, appUUID = client_id. */
			if (!appUUID) appUUID = oauthClientId;
		}

		/** Bail out if no application context was provided. */
		if (!appUUID) {
			console.warn('[Authorize] No appUUID resolved — showing error instead of hijacking to /Manage/', {
				querystring: $querystring,
				href: window.location.href,
				isOAuth,
				oauthClientId
			});
			errorMessage = t2('Missing application context. Please re-launch sign-in from the application.');
			loading = false;
			return;
		}

		/** For legacy (non-OAuth) flow, returnURL is required for security. */
		if (!isOAuth && !returnURL && !isPreview) {
			errorMessage = t2('Return URL is required for authorization.');
			loading = false;
			return;
		}

		/*
		 * Validate the application BEFORE checking auth. The public info
		 * endpoint is unauthenticated, so we can verify the app exists
		 * without sending the user through an entire login flow first.
		 */
		try {
			appInfo = await getApplicationPublicInfo(appUUID);
		} catch {
			errorMessage = t2('Application not found or is no longer active.');
			loading = false;
			return;
		}

		/* Redirect unauthenticated users to /login, passing the authorize
		   params (app, returnURL, oauth=…) through as top-level query
		   params. Login.svelte reconstructs the /authorize URL from these
		   after a successful sign-in.  We deliberately do NOT nest the
		   whole `/authorize?…` URL inside a `redirect=` parameter — that
		   produced a hard-to-read, doubly-percent-encoded URL bar
		   (`redirect=%2Fauthorize%3F…%26returnURL%3Dhttps%253A%252F…`)
		   that was confusing to debug and brittle against further
		   redirect hops. All state still lives in the URL — no
		   sessionStorage. */
		if (!getToken()) {
			/* Ensure `app=` is explicit on /login — in the OAuth flow the
			   identifier arrives as `client_id` and was upgraded into
			   `appUUID` above, but the param object still lacks an `app=`
			   key. Login.svelte uses `app` to fetch branded info. */
			const loginParams = new URLSearchParams(params);
			loginParams.set('app', appUUID);
			push(`/login?${loginParams.toString()}`);
			return;
		}

		/* For legacy (non-OAuth) flows, check if the user has already
		   authorized this application. If so, redirect immediately.
		   Set alreadyAuthorized as fallback for browser back navigation. */
		if (!isOAuth && !isPreview) {
			try {
				const authCheck = await checkApplicationAuthorization(appUUID);
				if (authCheck.isAuthorized) {
					alreadyAuthorized = true;
					await handleAuthorize();
					/* If redirect succeeded, page navigates away.
					   If not (browser back), alreadyAuthorized shows "Continue" UI. */
				}
			} catch {
				/* If check fails (e.g., network error), fall through to show consent screen. */
			}
		}

		/* For auto-approve apps (internal Abstract apps), skip the
		   consent screen and authorize immediately. */
		if (appInfo.isAutomaticApprovalEnabled && !isPreview) {
			try {
				await handleAuthorize();
				/* Keep loading=true — the redirect is happening, we don't want
				   to flash the consent screen while the browser navigates. */
				return;
			} catch {
				errorMessage = t2('Authorization failed. Please try again.');
				loading = false;
				return;
			}
		}

		loading = false;
	});

	// ── Handlers ──────────────────────────────────────────────

	/**
	 * Trigger a full-page navigation to an external URL while keeping a
	 * visible fallback link on screen. Browsers occasionally drop a
	 * programmatic `window.location.href` assignment (popup blockers,
	 * extensions, slow back/forward cache restores); recording the target
	 * lets the UI offer the user a manual click-through instead of
	 * stranding them on a blank or spinning page.
	 */
	function navigateExternal(url: string): void {
		pendingRedirectUrl = url;
		window.location.href = url;
		/* Defer the visible "Redirecting…" UI by 1s. On a fast redirect the
		   browser navigates away well before the timer fires and the user
		   never sees the message — which prevents a flicker between the
		   prior screen and the destination. Slow / blocked redirects still
		   surface the fallback link after the delay. */
		setTimeout(() => {
			showPendingRedirect = true;
			loading = false;
		}, 1000);
	}

	/**
	 * Redirect to the login page, encoding the current authorize URL
	 * as a query-string parameter so Login can redirect back after auth.
	 */
	function redirectToLogin(): void {
		const params = new URLSearchParams($querystring || '');
		/* `logout` is a one-shot directive — never carry it through into the
		   post-login bounce-back, otherwise we'd loop on the next round-trip. */
		params.delete('logout');
		/* Top-level params — see the onMount comment above for why we don't
		   nest the entire /authorize URL inside a `redirect=` parameter.
		   Ensure `app=` is explicit for the OAuth path where it was
		   inferred from `client_id`. */
		params.set('app', appUUID);
		push(`/login?${params.toString()}`);
	}

	/**
	 * Sign the current user out and bounce to /login while preserving the
	 * authorization context, so they can re-authenticate as a different
	 * account and land straight back on this consent page.
	 *
	 * The server session is revoked best-effort (the local token is cleared
	 * regardless), then {@link redirectToLogin} pushes to `/login?…` carrying
	 * the same authorize params. Login.svelte reconstructs the `/authorize`
	 * URL from those params and navigates back here once sign-in completes —
	 * no consent state is lost across the round-trip.
	 */
	async function handleSwitchAccount(): Promise<void> {
		switchingAccount = true;
		/* Revoke the active session (best-effort) and clear the local token
		   before bouncing to /login. Skip the network call if no token. */
		if (getToken()) {
			try {
				await serverLogout();
			} catch {
				/* Token may already be invalid server-side; continue with local cleanup. */
			}
			clearToken();
		}
		redirectToLogin();
	}

	// ── Handlers ──────────────────────────────────────────────

	/**
	 * Authorize the user for this application. Handles both legacy (JWT redirect)
	 * and OAuth 2.0 (authorization code redirect) flows.
	 */
	async function handleAuthorize(): Promise<void> {
		if (isPreview) {
			authResult = 'approved';
			return;
		}

		authorizing = true;
		errorMessage = '';
		/** Attempt authorization via the appropriate flow (OAuth or legacy). */
		try {
			if (isOAuth) {
				/* OAuth 2.0 flow: create authorization code, redirect with code. */
				const result = await createOAuthAuthorizationCode({
					client_id: oauthClientId,
					redirect_uri: oauthRedirectUri,
					scope: oauthScope,
					code_challenge: oauthCodeChallenge,
					code_challenge_method: oauthCodeChallengeMethod,
					...(oauthState ? { state: oauthState } : {}),
					...(oauthNonce ? { nonce: oauthNonce } : {})
				});

				/* Redirect back to the client with the authorization code. */
				const redirectUrl = new URL(result.redirect_uri);
				redirectUrl.searchParams.set('code', result.code);
				if (result.state) {
					redirectUrl.searchParams.set('state', result.state);
				}
				navigateExternal(redirectUrl.toString());
				return;
			}

			/* Legacy flow: authorize for app, redirect with JWT token. */
			const result = await authorizeForApplication(appUUID, returnURL || undefined);
			authResult = result.status;

			if (result.status === 'approved') {
				/* Mark as authorized so UI shows "Already Authorized" if redirect fails. */
				alreadyAuthorized = true;

				if (returnURL) {
					/* Hand over a freshly minted token, never the one the browser happens to hold:
					   that one carries the roles of whenever it was minted, so a role granted since
					   (a consumer app's admin role, say) would be missing and the app would refuse. */
					const token = await refreshTokenNow();
					if (returnURL === 'token') {
						/* Token flow: show staging page instead of redirecting. */
						tokenDisplay = token || '';
						return;
					}
					/* Redirect back to the consumer application with the token. */
					const separator = returnURL.includes('?') ? '&' : '?';
					const redirectTo = `${returnURL}${separator}token=${encodeURIComponent(token || '')}`;
					navigateExternal(redirectTo);
					return;
				}
			}
		} catch (err: any) {
			/* If the token expired or is invalid, redirect to login. */
			if (err?.message?.includes('not authenticated') || err?.status === 401) {
				redirectToLogin();
				return;
			}
			errorMessage = err?.message || t2('Authorization failed. Please try again.');
		} finally {
			authorizing = false;
		}
	}

	/**
	 * Deny access. For OAuth flows, redirect back with error=access_denied per
	 * RFC 6749 §4.1.2.1. For legacy flows, redirect with denied=true or navigate
	 * back to the dashboard.
	 */
	function handleDeny(): void {
		if (isPreview) {
			window.close();
			return;
		}

		/** For OAuth flows, redirect back to the client with an access_denied error. */
		if (isOAuth) {
			/* OAuth 2.0: redirect with error per RFC 6749. */
			const redirectUrl = new URL(oauthRedirectUri);
			redirectUrl.searchParams.set('error', 'access_denied');
			redirectUrl.searchParams.set('error_description', 'The user denied the authorization request.');
			if (oauthState) {
				redirectUrl.searchParams.set('state', oauthState);
			}
			navigateExternal(redirectUrl.toString());
			return;
		}

		/** For legacy flows, redirect to the return URL with denied flag or fall back to dashboard. */
		if (returnURL && returnURL !== 'token') {
			const separator = returnURL.includes('?') ? '&' : '?';
			navigateExternal(`${returnURL}${separator}denied=true`);
		} else {
			/* Cross-app navigation to the admin app. */
			navigateExternal('/Manage/');
		}
	}

	/** Copy the displayed token to the clipboard and flash feedback. */
	async function copyToken(): Promise<void> {
		await navigator.clipboard.writeText(tokenDisplay);
		copied = true;
		setTimeout(() => (copied = false), 2000);
	}

	/**
	 * After pending approval, navigate to the dashboard.
	 */
	function handleContinue(): void {
		/* Cross-app navigation to the admin app. */
		navigateExternal('/Manage/');
	}
</script>

<!--
  "Signed in as … — use a different account" footer.

  Rendered on the screens where a session is active (consent prompt and
  the already-authorized continue screen). Lets the user log out and
  re-authenticate without losing the authorization context: the consent
  params are carried through to /login and back. See handleSwitchAccount.
-->
{#snippet switchAccountFooter()}
	<div class="mt-6 border-t border-gray-100 pt-4 text-center dark:border-gray-700">
		{#if $tokenData?.email}
			<p class="text-xs text-gray-400 dark:text-gray-500">
				{$t("Signed in as")} <strong class="text-gray-500 dark:text-gray-400">{$tokenData.email}</strong>
			</p>
		{/if}
		<button
			type="button"
			class="mt-1 inline-flex items-center text-xs font-medium text-primary-600 hover:underline disabled:opacity-60 dark:text-primary-400"
			disabled={switchingAccount || authorizing}
			onclick={handleSwitchAccount}
		>
			{#if switchingAccount}
				<Spinner size="4" class="mr-1.5" />
			{:else}
				<i class="fa-solid fa-right-left mr-1.5"></i>
			{/if}
			{$t("Use a different account")}
		</button>
	</div>
{/snippet}

<AuthLayout heading="" subheading="">
	{#if showPendingRedirect}
		<!--
		  Fallback for browsers that drop the programmatic redirect
		  (popup blockers, slow networks, restored bfcache pages).
		  The page already issued `window.location.href = pendingRedirectUrl`;
		  this block stays visible so the user can recover by clicking the
		  link manually instead of being stranded on a blank screen.
		-->
		<div class="text-center">
			<div
				class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30"
			>
				<Spinner size="6" />
			</div>
			<h2 class="mb-2 text-xl font-bold text-gray-900 dark:text-white">
				{$t("Redirecting…")}
			</h2>
			<p class="mb-6 text-sm text-gray-500 dark:text-gray-400">
				{#if appInfo}
					{$t("Sending you back to")}
					<strong class="text-gray-700 dark:text-gray-300">{appInfo.applicationName}</strong>.
				{:else}
					{$t("Sending you to your destination.")}
				{/if}
			</p>
			<a
				href={pendingRedirectUrl}
				class="inline-flex w-full items-center justify-center rounded-lg bg-primary-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-primary-800 focus:outline-none focus:ring-4 focus:ring-primary-300 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800"
			>
				{$t("Click here if you are not redirected automatically")}
				<i class="fa-solid fa-arrow-right ml-2"></i>
			</a>
		</div>
	{:else if loading}
		<LoadingOverlay />
	{/if}

	{#if errorMessage && !loading && !showPendingRedirect}
		<AlertMessage type="error" message={errorMessage} onclose={() => (errorMessage = '')} />
		<div class="mt-4 text-center">
			<Button color="alternative" onclick={() => push('/login')}>{$t("Back to sign in")}</Button>
		</div>
	{/if}

	{#if appInfo && !loading && !showPendingRedirect}
		<!-- Pending approval result -->
		{#if authResult === 'pending'}
			<div class="text-center">
				<div
					class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/30"
				>
					<i class="fa-solid fa-clock text-2xl text-yellow-500"></i>
				</div>
				<h2 class="mb-2 text-xl font-bold text-gray-900 dark:text-white">{$t("Awaiting Approval")}</h2>
				<p class="mb-6 text-gray-500 dark:text-gray-400">
					{$t("Your request to access")} <strong class="text-gray-700 dark:text-gray-300"
						>{appInfo.applicationName}</strong
					>
					{$t("has been submitted. An administrator will review and approve your access.")}
				</p>
				<Button color="primary" class="w-full" onclick={handleContinue}>{$t("Continue to Dashboard")}</Button>
			</div>

			<!-- Preview-mode token flow result -->
		{:else if authResult === 'approved' && isPreview && returnURL === 'token'}
			<div class="text-center">
				<div
					class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30"
				>
					<i class="fa-solid fa-clipboard text-2xl text-blue-500"></i>
				</div>
				<h2 class="mb-2 text-xl font-bold text-gray-900 dark:text-white">{$t("Token Flow Preview")}</h2>
				<p class="mb-4 text-sm text-gray-500 dark:text-gray-400">
					{$t("This is a preview of the token staging page. In the real flow, the user's JWT would appear below.")}
				</p>
				<div class="relative mb-4">
					<textarea
						readonly
						rows="4"
						class="w-full cursor-pointer rounded-lg border border-gray-300 bg-depth p-3 font-mono text-xs text-gray-600 dark:border-gray-600 dark:text-gray-300"
						onclick={async () => { await navigator.clipboard.writeText(previewToken); copied = true; setTimeout(() => (copied = false), 2000); }}
						onfocus={(e) => (e.target as HTMLTextAreaElement).select()}
					>{previewToken}</textarea>
					{#if copied}
						<div class="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-green-600 px-3 py-1 text-xs font-medium text-white shadow-lg">
							<i class="fa-solid fa-check me-1"></i>{$t("Copied to clipboard")}
						</div>
					{/if}
				</div>
				<div
					class="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20"
				>
					<p class="text-xs text-blue-700 dark:text-blue-300">
						<i class="fa-solid fa-info-circle me-1"></i>
						{$t("This is a preview. No real token was generated.")}
					</p>
				</div>
				<div class="flex gap-3">
					<Button color="primary" class="flex-1" onclick={async () => { await navigator.clipboard.writeText(previewToken); copied = true; setTimeout(() => (copied = false), 2000); }}>
						<i class="fa-solid fa-clipboard me-2"></i>{$t("Copy to Clipboard")}
					</Button>
					<Button color="alternative" class="flex-1" onclick={() => window.close()}>{$t("Close Preview")}</Button>
				</div>
			</div>

			<!-- Preview-mode approved result (redirect flow) -->
		{:else if authResult === 'approved' && isPreview}
			<div class="text-center">
				<div
					class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30"
				>
					<i class="fa-solid fa-check text-2xl text-green-500"></i>
				</div>
				<h2 class="mb-2 text-xl font-bold text-gray-900 dark:text-white">{$t("Access Granted")}</h2>
				<p class="mb-2 text-gray-500 dark:text-gray-400">
					{$t("This is a preview. In the real flow, the user would be redirected back to")}
					<strong class="text-gray-700 dark:text-gray-300">{appInfo.applicationName}</strong>.
				</p>
				<Button color="alternative" class="mt-4 w-full" onclick={() => window.close()}>{$t("Close Preview")}</Button>
			</div>

			<!-- Token staging page (real flow, returnURL=token) -->
		{:else if authResult === 'approved' && tokenDisplay}
			<div class="text-center">
				<div
					class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30"
				>
					<i class="fa-solid fa-check text-2xl text-green-500"></i>
				</div>
				<h2 class="mb-2 text-xl font-bold text-gray-900 dark:text-white">{$t("Authorization Successful")}</h2>
				<p class="mb-4 text-sm text-gray-500 dark:text-gray-400">
					{$t("Copy the token below and paste it into")}
					<strong class="text-gray-700 dark:text-gray-300">{appInfo.applicationName}</strong>
					{$t("to complete sign-in.")}
				</p>
				<div class="relative mb-4">
					<textarea
						readonly
						rows="4"
						class="w-full cursor-pointer rounded-lg border border-gray-300 bg-depth p-3 font-mono text-xs text-gray-600 dark:border-gray-600 dark:text-gray-300"
						onclick={copyToken}
						onfocus={(e) => (e.target as HTMLTextAreaElement).select()}
					>{tokenDisplay}</textarea>
					{#if copied}
						<div class="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-green-600 px-3 py-1 text-xs font-medium text-white shadow-lg">
							<i class="fa-solid fa-check me-1"></i>{$t("Token copied to clipboard")}
						</div>
					{/if}
				</div>
				<div class="flex gap-3">
					<Button color="primary" class="flex-1" onclick={copyToken}>
						<i class="fa-solid fa-clipboard me-2"></i>{$t("Copy to Clipboard")}
					</Button>
					<Button color="alternative" class="flex-1" onclick={() => (window.location.href = '/Manage/')}>
						{$t("Done")}
					</Button>
				</div>
			</div>

			<!-- Already authorized - simplified continue flow -->
		{:else if alreadyAuthorized}
			<div class="text-center">
				{#if appInfo.logoImageName}
					<img
						src="{apiBase}/uploads/applications/{appInfo.logoImageName}"
						alt={appInfo.applicationName}
						class="mx-auto mb-4 h-20 w-20 rounded-xl object-contain dark:invert"
					/>
				{:else if appInfo.iconImageName}
					<img
						src="{apiBase}/uploads/applications/{appInfo.iconImageName}"
						alt={appInfo.applicationName}
						class="mx-auto mb-4 h-16 w-16 rounded-lg object-contain dark:invert"
					/>
				{:else}
					<div
						class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-xl bg-green-100 dark:bg-green-900/30"
					>
						<i class="fa-solid fa-check text-2xl text-green-600 dark:text-green-400"></i>
					</div>
				{/if}

				<h2 class="mb-2 text-xl font-bold text-gray-900 dark:text-white">
					{$t("Already Authorized")}
				</h2>
				<p class="mb-6 text-gray-500 dark:text-gray-400">
					{$t("You've previously granted access to")}
					<strong class="text-gray-700 dark:text-gray-300">{appInfo.applicationName}</strong>.
				</p>

				<Button color="primary" class="w-full" onclick={handleAuthorize} disabled={authorizing}>
					{#if authorizing}
						<Spinner size="4" class="mr-2" />
					{/if}
					{$t("Continue to")} {appInfo.applicationName}
				</Button>

				{@render switchAccountFooter()}
			</div>

			<!-- Consent prompt -->
		{:else}
			<div class="mb-6 text-center">
				{#if appInfo.logoImageName}
					<img
						src="{apiBase}/uploads/applications/{appInfo.logoImageName}"
						alt={appInfo.applicationName}
						class="mx-auto mb-4 h-40 w-auto max-w-full object-contain dark:invert"
					/>
				{:else if appInfo.iconImageName}
					<img
						src="{apiBase}/uploads/applications/{appInfo.iconImageName}"
						alt={appInfo.applicationName}
						class="mx-auto mb-4 h-16 w-16 rounded-lg object-contain dark:invert"
					/>
				{:else}
					<div
						class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-xl bg-primary-100 dark:bg-primary-900/30"
					>
						<i class="fa-solid {appInfo.icon || 'fa-cube'} text-2xl text-primary-600 dark:text-primary-400"
						></i>
					</div>
				{/if}

				{#if !appInfo.logoImageName}
					<h2 class="text-xl font-bold text-gray-900 dark:text-white">
						{$t("Sign in to")} {appInfo.applicationName}
					</h2>
				{/if}

				{#if appInfo.description}
					<p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
						{appInfo.description}
					</p>
				{/if}
			</div>

			<AbstractCard class="bg-depth mb-6 max-w-none p-4">
				<p class="text-sm text-gray-600 dark:text-gray-300">
					<strong class="text-gray-800 dark:text-white">{appInfo.applicationName}</strong>
					{$t("is requesting access to your account. By continuing, you allow this application to verify your identity")}{isOAuth
						? $t(' and access the following')
						: $t(' and access your profile information')}.
				</p>

				{#if isOAuth && oauthScope}
					<ul class="mt-3 space-y-2">
						{#each oauthScope.split(' ').filter(Boolean) as scope (scope)}
							<li class="flex items-center text-sm text-gray-700 dark:text-gray-300">
								<i
									class="fa-solid {scope === 'openid'
										? 'fa-id-badge'
										: scope === 'profile'
											? 'fa-user'
											: scope === 'email'
												? 'fa-envelope'
												: scope === 'roles'
													? 'fa-shield-halved'
													: 'fa-key'} mr-2 w-5 text-primary-500"
								></i>
								{#if scope === 'openid'}
									<span><strong>OpenID</strong> — {$t("Verify your identity")}</span>
								{:else if scope === 'profile'}
									<span><strong>{$t("Profile")}</strong> — {$t("Name, username, and avatar")}</span>
								{:else if scope === 'email'}
									<span><strong>{$t("Email")}</strong> — {$t("Email address and verification status")}</span>
								{:else if scope === 'roles'}
									<span><strong>{$t("Roles")}</strong> — {$t("Your assigned roles for this application")}</span>
								{:else}
									<span><strong>{scope}</strong></span>
								{/if}
							</li>
						{/each}
					</ul>
				{/if}
			</AbstractCard>

			{#if isPreview}
				<div
					class="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20"
				>
					<p class="text-xs text-blue-700 dark:text-blue-300">
						<i class="fa-solid fa-eye me-1"></i>
						{$t("This is a preview of the consent page. No authorization will be performed.")}
					</p>
				</div>
			{/if}

			<div class="space-y-3">
				<Button color="primary" class="w-full" onclick={handleAuthorize} disabled={authorizing}>
					{#if authorizing}
						<Spinner size="4" class="mr-2" />
					{/if}
					{$t("Authorize")} {appInfo.applicationName}
				</Button>

				<Button color="alternative" class="w-full" onclick={handleDeny}>{$t("Deny")}</Button>
			</div>

			{#if appInfo.website}
				<div class="mt-4 text-center">
					<a
						href={appInfo.website}
						target="_blank"
						rel="noopener noreferrer"
						class="text-xs text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
					>
						{appInfo.website} <i class="fa-solid fa-arrow-up-right-from-square ml-1"></i>
					</a>
				</div>
			{/if}

			{@render switchAccountFooter()}
		{/if}
	{/if}
</AuthLayout>
